import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { CreateAgencyFormType, createAgencySchema } from "../schema/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import AgentAgencyForm from "./AgentAgencyForm";
import Typography from "@mui/joy/Typography";
import Stack from "@mui/joy/Stack";
import SearchComponent from "./SearchComponent";
import axiosInstance from "@/services/api/axiosInstance";
import { toast } from "sonner";
import StickyFooter from "@/components/StickyFooter/StickyFooter";
import Button from "@mui/joy/Button";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import LinkButton from "@/components/LinkButton/LinkButton";
import { ADMIN_ROUTE_PREFIX } from "@/types/constants/common";
import { UserObj } from "@/types/interfaces/common";

const CreateAgencyForm = () => {
  const [agent, setAgent] = useState<UserObj | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (agent !== null) {
      const {
        firstname,
        lastname,
        ttandEmail,
        businessPhone,
        agentId,
        clientlinqBranch,
        city,
        province,
        postalCode,
        streetAddress,
        brandedAs,
      } = agent;
      setValue("firstname", firstname ?? "");
      setValue("lastname", lastname ?? "");
      setValue("ttandEmail", ttandEmail ?? "");
      setValue("businessPhone", businessPhone?.replace(/-/g, "") ?? "");
      setValue("city", city ?? "");
      setValue("province", province ?? "");
      setValue("postalCode", postalCode ?? "");
      setValue("streetAddress", streetAddress ?? "");
      setValue("brandedAs", brandedAs ?? "");
      setValue("agentId", `${agentId}`);
      setValue("clientlinqBranch", clientlinqBranch);
    }
  }, [agent]);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<CreateAgencyFormType>({
    resolver: zodResolver(createAgencySchema),
    defaultValues: {
      firstname: "",
      lastname: "",
      agentId: "",
      streetAddress: "",
      city: "",
      province: "",
      postalCode: "",
      ttandEmail: "",
      businessPhone: "",
      brandedAs: "",
      clientlinqBranch: "",
    },
  });

  const onSubmit = async (data: CreateAgencyFormType) => {
    setIsSubmitting(true);
    try {
      // format for creatAgency -> constria
      const formatedData = {
        AgencyName: data.brandedAs,
        SourceAgencyId: data.clientlinqBranch,
        Address: {
          Address1: data.streetAddress,
          Address2: "",
          City: data.city,
          State: data.province,
          Zip: data.postalCode,
          Country: "CA",
        },
        Phone: data.businessPhone,
        Email: data.ttandEmail,
        Website: "",
        MainContact: {
          FirstName: data.firstname,
          LastName: data.lastname,
          Email: data.ttandEmail,
          Phone: data.businessPhone,
          SourceAgentId: data.agentId,
        },
      };
      const result = await axiosInstance.post(`/admin/constria/create-agency`, formatedData);
      toast.success(result.data?.message || "Agency Created Successfully");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something Went Wrong");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Stack>
      <Typography
        level="h3"
        sx={{
          mb: 4,
        }}
      >
        {"Create Agency"}
      </Typography>
      <SearchComponent type={"agency"} onDataFetched={setAgent} mode={"create"} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction={"row"} gap={4}>
          <AgentAgencyForm
            mode={"create"}
            type={"agency"}
            register={register}
            control={control}
            setValue={setValue}
            setError={setError}
            errors={errors}
            disable={false}
          />
        </Stack>
        <StickyFooter sx={{ mt: 4 }}>
          <Button
            type="submit"
            sx={{ textTransform: "capitalize" }}
            loading={isSubmitting}
            disabled={isSubmitting}
            startDecorator={<AddOutlinedIcon />}
          >
            Create Agency
          </Button>
          <LinkButton color="neutral" variant="outlined" to={`${ADMIN_ROUTE_PREFIX}/`} disabled={isSubmitting}>
            Cancel
          </LinkButton>
        </StickyFooter>
      </form>
    </Stack>
  );
};

export default CreateAgencyForm;
